import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  downloadNewsletterCsv,
  getNewsletterPreview,
  type NewsletterRequestItem,
} from "@/api/newsletter";
import AdminLayout from "@/components/admin/AdminLayout";

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIsoDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("es-ES");
}

function actionLabel(action: NewsletterRequestItem["action"]): string {
  return action === "alta" ? "Alta" : "Baja";
}

export default function AdminNewsletter() {
  const [startDate, setStartDate] = useState(daysAgoIsoDate(7));
  const [endDate, setEndDate] = useState(todayIsoDate());
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<NewsletterRequestItem[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currentRangeKey = `${startDate}:${endDate}`;
  const hasPreviewForRange = previewKey === currentRangeKey;

  const resetPreview = () => {
    setPreviewKey(null);
    setPreviewRows([]);
    setSuccess(null);
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (field === "start") setStartDate(value);
    else setEndDate(value);
    resetPreview();
    setError(null);
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (endDate < startDate) {
      setError("La fecha «Hasta» debe ser igual o posterior a «Desde».");
      resetPreview();
      return;
    }

    setIsPreviewLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await getNewsletterPreview(startDate, endDate);
      setPreviewRows(data.items);
      setPreviewKey(currentRangeKey);
    } catch (err: unknown) {
      resetPreview();
      if (err instanceof ApiError && err.message.includes("does not exist")) {
        setError("La tabla newsletter_requests no existe. Ejecuta: make migrate");
      } else {
        setError(err instanceof Error ? err.message : "No se pudo cargar la vista previa.");
      }
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!hasPreviewForRange || isDownloading) return;

    setIsDownloading(true);
    setError(null);
    setSuccess(null);

    try {
      await downloadNewsletterCsv(startDate, endDate);
      setSuccess("CSV descargado correctamente.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "No se pudo descargar el CSV.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <AdminLayout title="Newsletter">
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium">Exportar altas y bajas</h2>
        <p className="mt-2 text-sm text-slate-600">
          Consulta las solicitudes del rango seleccionado y descarga el CSV cuando la vista previa
          sea correcta.
        </p>

        <form className="mt-6 grid gap-4 md:grid-cols-3" onSubmit={handlePreview}>
          <div>
            <label htmlFor="newsletter-start" className="block text-sm font-medium text-slate-700">
              Desde
            </label>
            <input
              id="newsletter-start"
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              required
            />
          </div>
          <div>
            <label htmlFor="newsletter-end" className="block text-sm font-medium text-slate-700">
              Hasta
            </label>
            <input
              id="newsletter-end"
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              required
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={isPreviewLoading}
              className="rounded-lg bg-slate-800 px-4 py-2 font-medium text-white hover:bg-slate-900 disabled:opacity-50"
            >
              {isPreviewLoading ? "Cargando…" : "Ver vista previa"}
            </button>
          </div>
        </form>

        {hasPreviewForRange ? (
          <div className="mt-8 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                {previewRows.length === 0
                  ? "No hay solicitudes en este rango."
                  : `${previewRows.length} solicitud(es) encontrada(s).`}
              </p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={isDownloading}
                className="rounded-lg bg-emerald-700 px-4 py-2 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                {isDownloading ? "Descargando…" : "Descargar CSV"}
              </button>
            </div>

            {previewRows.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Acción</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Registrado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {previewRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 font-medium">{row.email}</td>
                        <td className="px-4 py-3">{actionLabel(row.action)}</td>
                        <td className="px-4 py-3">{row.status}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatDateTime(row.inserted_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="mt-4 text-sm text-emerald-700" role="status">
            {success}
          </p>
        ) : null}
      </section>
    </AdminLayout>
  );
}
